/**
 * SEO/AEO landing-page data (Jose 2026-09-16) — Tier-1 keyword pages from the Local Dominance audit.
 * Each renders via SeoLandingPage with React-19 native per-page <title>/<meta>/<link canonical> + a
 * Service + FAQPage JSON-LD block, so Google + AI answer engines can rank/quote each intent.
 * SLIK-specific copy (uses the real client verbiage); make config-driven when multi-tenant.
 */
export interface SeoLandingFaq { q: string; a: string; }
export interface SeoLanding {
  slug: string;
  title: string;          // <title> — keyword + brand + location
  description: string;    // <meta description>
  h1: string;
  sub: string;
  intro: string;
  bullets: string[];
  serviceName: string;
  price?: string;
  faqs: SeoLandingFaq[];
}

const BASE = 'https://book.slikbarberlounge.com';

export const CANONICAL_BASE = BASE;

export const SEO_LANDINGS: SeoLanding[] = [
  {
    slug: 'skin-fade-florham-park',
    title: 'Skin Fade in Florham Park, NJ | SLIK Barber Lounge',
    description: 'Florham Park’s specialists in skin fades & taper fades — clean crisp lines, all modern hairstyles, scissor work on top. Book online at SLIK Barber Lounge.',
    h1: 'Skin Fades in Florham Park, NJ',
    sub: 'Skin-tight fades · taper fades · all modern hairstyles',
    intro: 'SLIK Barber Lounge is Florham Park’s home for the perfect skin fade. Our master barbers — Jose, Diana, Edgar and the team — are known for clean crisp lines, seamless blending, and scissor work on top. Book online.',
    bullets: ['Skin fades & taper fades', 'Clean crisp lines & all modern hairstyles', 'Blending + scissors on top', 'Beard shaping to match your cut'],
    serviceName: 'Skin Fade',
    faqs: [
      { q: 'Where can I get a skin fade in Florham Park, NJ?', a: 'SLIK Barber Lounge at 184 Columbia Turnpike, Florham Park specializes in skin fades and taper fades. Book online at slikbarberlounge.com.' },
      { q: 'What makes SLIK’s skin fades stand out?', a: 'Clean crisp lines, seamless blending, and scissor work on top — dialed to your head shape and style. Reserve your barber online.' },
      { q: 'Do I need an appointment for a fade?', a: 'Walk-ins are welcome when a barber is available, but we book out about a week ahead — booking online guarantees your barber and time.' },
    ],
  },
  {
    slug: 'beard-trim-florham-park',
    title: 'Beard Trim & Shaping in Florham Park, NJ | SLIK Barber Lounge',
    description: 'Beard shaping, line-ups & hot-towel shaves in Florham Park, NJ. Precise, clean beard work at SLIK Barber Lounge — book online.',
    h1: 'Beard Trim & Shaping in Florham Park, NJ',
    sub: 'Beard shaping · line-ups · hot-towel shaves',
    intro: 'Sharp, clean beard work at SLIK Barber Lounge in Florham Park. From a precise beard shape-up to a classic hot-towel gentleman’s shave, our barbers finish every cut right.',
    bullets: ['Beard shaping & line-ups', 'Hot-towel shave', 'Gentleman’s straight-razor finish', 'Pairs with any haircut or fade'],
    serviceName: 'Beard Shaping',
    faqs: [
      { q: 'Where can I get a beard trim in Florham Park, NJ?', a: 'SLIK Barber Lounge, 184 Columbia Turnpike, Florham Park, offers beard shaping, line-ups, and hot-towel shaves. Book online.' },
      { q: 'What beard services does SLIK offer?', a: 'Beard shaping and line-ups, a classic hot-towel shave, and a straight-razor finish — on their own or added to any cut. Book online.' },
      { q: 'Do you do hot-towel shaves?', a: 'Yes — we offer a classic hot-towel gentleman’s shave, and The Whiskey Works package (haircut + shave + barber facial).' },
    ],
  },
  {
    slug: 'kids-haircut-florham-park',
    title: 'Kids Haircuts in Florham Park, NJ | SLIK Barber Lounge',
    description: 'SLIK Barber Lounge is a men’s barber shop in Florham Park — bring the boys. Father & Son and Father & 2 Kids cuts for kids who can sit on their own. Relax in the lounge while they’re in the chair.',
    h1: 'Kids Haircuts in Florham Park, NJ',
    sub: 'Men’s barber shop · Father & Son · kids who can sit on their own',
    intro: 'SLIK is a men’s barber shop first — but bring the boys. As long as your kid can sit on their own, they get the same sharp cut you do while you relax in the lounge. Grab a Father & Son and knock it out together.',
    bullets: ['A men’s barber shop', 'Father & Son cuts', 'Kids who can sit on their own', 'Relax in the lounge'],
    serviceName: 'Kids Haircut',
    faqs: [
      { q: 'Do you cut kids at SLIK Barber Lounge?', a: 'Yes — as long as your kid can sit on their own, we’ll line them up. We’re a men’s barber shop, not a kids’ salon, but the boys are in good hands. Book online at slikbarberlounge.com.' },
      { q: 'Where do I wait while my kid gets cut?', a: 'Relax in the lounge — you’re welcome the whole time while your kid’s in the chair.' },
      { q: 'Is there a Father & Son deal?', a: 'Yep — book the Father & Son when you reserve online, or just ask in the shop.' },
    ],
  },
  {
    slug: 'barber-east-hanover-nj',
    title: 'Barber near East Hanover, NJ | SLIK Barber Lounge (Florham Park)',
    description: 'Looking for a top barber near East Hanover, NJ? SLIK Barber Lounge in neighboring Florham Park — skin fades, beard shaping, hot-towel shaves. Book online.',
    h1: 'A Top Barber near East Hanover, NJ',
    sub: 'Skin fades · beard shaping · hot-towel shaves — minutes from East Hanover',
    intro: 'Just over the line in Florham Park, SLIK Barber Lounge is an easy trip for East Hanover clients who want a 5-star cut. Skin fades, all modern hairstyles, beard shaping, and hot-towel shaves — book online.',
    bullets: ['Minutes from East Hanover (07936)', 'Skin fades & taper fades', 'Beard shaping & hot-towel shaves', 'Online booking'],
    serviceName: 'Haircut',
    faqs: [
      { q: 'Is there a good barber near East Hanover, NJ?', a: 'SLIK Barber Lounge in neighboring Florham Park (184 Columbia Turnpike) is minutes from East Hanover and specializes in skin fades and beard work. Book online.' },
      { q: 'How far is SLIK from East Hanover?', a: 'SLIK is just over the border in Florham Park on Columbia Turnpike — a short drive from East Hanover, 07936.' },
    ],
  },
];
